// Two-way linking between a quote line's total (qty x unitCost) and its
// labour / material / plant breakdown. Keeps the same proportions when the
// total changes, and back-computes a new unitCost when a single component
// is edited. The CATEGORY_DEFAULT_SPLITS table mirrors the server table in
// artifacts/api-server/src/replit_integrations/quote-ai/index.ts -- keep the
// two in sync. Sums to 1.0 within each entry.

export type CostSplit = { labour: number; material: number; plant: number };
import { fitQuotedCostSplit } from "@workspace/api-zod/quote-cost-split";

export const CATEGORY_DEFAULT_SPLITS: Record<string, CostSplit> = {
  bathrooms: { labour: 0.30, material: 0.65, plant: 0.05 },
  brickwork: { labour: 0.55, material: 0.40, plant: 0.05 },
  carpentry: { labour: 0.55, material: 0.40, plant: 0.05 },
  damp_proofing: { labour: 0.50, material: 0.45, plant: 0.05 },
  decorating: { labour: 0.70, material: 0.25, plant: 0.05 },
  demolition: { labour: 0.45, material: 0.05, plant: 0.50 },
  electrical: { labour: 0.65, material: 0.30, plant: 0.05 },
  electrics: { labour: 0.65, material: 0.30, plant: 0.05 },
  equipment_hire: { labour: 0.10, material: 0.05, plant: 0.85 },
  fences_barriers: { labour: 0.40, material: 0.55, plant: 0.05 },
  fire_protection: { labour: 0.55, material: 0.40, plant: 0.05 },
  flat_roofing: { labour: 0.50, material: 0.45, plant: 0.05 },
  flooring: { labour: 0.45, material: 0.50, plant: 0.05 },
  formwork: { labour: 0.55, material: 0.40, plant: 0.05 },
  foundations: { labour: 0.45, material: 0.25, plant: 0.30 },
  groundworks: { labour: 0.45, material: 0.25, plant: 0.30 },
  heating: { labour: 0.40, material: 0.55, plant: 0.05 },
  hoarding: { labour: 0.40, material: 0.55, plant: 0.05 },
  insulation: { labour: 0.40, material: 0.55, plant: 0.05 },
  internal_walls: { labour: 0.55, material: 0.40, plant: 0.05 },
  kitchens: { labour: 0.30, material: 0.65, plant: 0.05 },
  legal: { labour: 1.00, material: 0.00, plant: 0.00 },
  legal_fees: { labour: 1.00, material: 0.00, plant: 0.00 },
  making_good: { labour: 0.70, material: 0.30, plant: 0.00 },
  plant: { labour: 0.10, material: 0.05, plant: 0.85 },
  plastering: { labour: 0.65, material: 0.30, plant: 0.05 },
  plumbing: { labour: 0.55, material: 0.40, plant: 0.05 },
  preliminaries: { labour: 0.50, material: 0.10, plant: 0.40 },
  preparation_protection: { labour: 0.50, material: 0.45, plant: 0.05 },
  protection: { labour: 0.50, material: 0.45, plant: 0.05 },
  roofing: { labour: 0.50, material: 0.45, plant: 0.05 },
  scaffolding: { labour: 0.30, material: 0.05, plant: 0.65 },
  site_equipment: { labour: 0.10, material: 0.05, plant: 0.85 },
  skip: { labour: 0.10, material: 0.05, plant: 0.85 },
  steel: { labour: 0.30, material: 0.55, plant: 0.15 },
  stud_walls: { labour: 0.55, material: 0.40, plant: 0.05 },
  tiling: { labour: 0.55, material: 0.40, plant: 0.05 },
  ventilation: { labour: 0.50, material: 0.45, plant: 0.05 },
  wall_formwork: { labour: 0.55, material: 0.40, plant: 0.05 },
  waste: { labour: 0.10, material: 0.05, plant: 0.85 },
  windows_doors: { labour: 0.30, material: 0.65, plant: 0.05 },
};

const FALLBACK_SPLIT: CostSplit = { labour: 0.60, material: 0.30, plant: 0.10 };

function normaliseCategory(c: string | null | undefined): string {
  return (c ?? "").toString().trim().toLowerCase().replace(/[\s-]+/g, "_");
}

export function getDefaultSplit(category: string | null | undefined): CostSplit {
  return CATEGORY_DEFAULT_SPLITS[normaliseCategory(category)] ?? FALLBACK_SPLIT;
}

// Round to 2dp (cents). Currency values are stored as numbers; rounding to
// the nearest penny keeps L+M+P in lock-step with newSubtotal without
// accumulating floating-point drift.
function r2(n: number): number {
  return Math.round(n * 100) / 100;
}

// Given a previous breakdown and a new total, return a re-distributed
// breakdown that preserves the previous ratios. If the previous breakdown
// summed to zero (e.g. a freshly added manual item), fall back to the
// category default split. The remainder after rounding material+plant to 2dp
// is absorbed by labour so labour+material+plant === r2(newSubtotal) exactly
// (modulo IEEE-754 noise).
export function rescaleBreakdown(
  prev: { labour: number; material: number; plant: number },
  newSubtotal: number,
  fallbackCategory?: string | null,
): CostSplit {
  const target = r2(Math.max(0, newSubtotal));
  const prevSum = prev.labour + prev.material + prev.plant;
  let matRatio: number;
  let pltRatio: number;
  if (prevSum > 0) {
    matRatio = prev.material / prevSum;
    pltRatio = prev.plant / prevSum;
  } else {
    const fallback = getDefaultSplit(fallbackCategory);
    matRatio = fallback.material;
    pltRatio = fallback.plant;
  }
  const split = fitQuotedCostSplit(target, { materialCost: target * matRatio, plantCost: target * pltRatio });
  return { labour: split.labourCost, material: split.materialCost, plant: split.plantCost };
}

// Sum of the three components -- the line subtotal implied by the breakdown.
export function subtotalFromBreakdown(
  labour: number,
  material: number,
  plant: number,
): number {
  return (Number(labour) || 0) + (Number(material) || 0) + (Number(plant) || 0);
}

// Back-compute unitCost from a breakdown total and a quantity. Returns the
// previous unitCost when quantity is non-positive so callers can avoid
// division-by-zero artefacts.
export function unitCostFromBreakdown(
  labour: number,
  material: number,
  plant: number,
  quantity: number,
  prevUnitCost: number,
): number {
  const qty = Number(quantity) || 0;
  if (qty <= 0) return prevUnitCost;
  const total = subtotalFromBreakdown(labour, material, plant);
  return total / qty;
}
